import React from "react";
import { legalCompanyDetails } from "./constants";
import type { LegalDocumentDefinition } from "./types";

const {
  brandName,
  companyRegistrationNumber,
  contactEmail,
  governingLaw,
  jurisdiction,
  legalEmail,
  legalEntityName,
  registeredAddress,
  serviceName,
  vatNumber,
} = legalCompanyDetails;

export const tosDocument: LegalDocumentDefinition = {
  id: "tos",
  title: "Terms of Service",
  slug: "/tos",
  effectiveDate: "2026-06-13",
  render: (renderLink) => (
    <>
      <p>
        These Terms of Service ("Terms") govern your access to and use of {serviceName} (the "Service"). By using the
        Service, you agree to these Terms.
      </p>

      <h5>1. Who we are</h5>
      <p>
        The Service is provided by <strong>{legalEntityName}</strong>. Registered address:{" "}
        <strong>{registeredAddress}</strong>. Company registration number:{" "}
        <strong>{companyRegistrationNumber}</strong>
        {vatNumber ? <>. VAT number: <strong>{vatNumber}</strong></> : null}. Legal notices can be sent to{" "}
        <code>{legalEmail}</code>; general questions can be sent to <code>{contactEmail}</code>.
      </p>
      <p>
        For information about how we handle personal data, see our{" "}
        {renderLink({ href: "/privacy", children: "Privacy Policy" })}.
      </p>

      <h5>2. Eligibility and accounts</h5>
      <ul>
        <li>
          You must be at least 18 years old to use the Service in your own right, as this is the age of contractual
          capacity in {governingLaw}. If you are under 18, a parent or legal guardian must
          agree to these Terms on your behalf and take responsibility for the account. This is a separate rule from
          the data-collection age threshold described in our{" "}
          {renderLink({ href: "/privacy", children: "Privacy Policy" })} §12.
        </li>
        <li>You are responsible for your account, credentials, household access, and activity under your account.</li>
        <li>You must provide accurate information and keep it up to date.</li>
        <li>You must tell us promptly if you believe your account has been compromised.</li>
      </ul>

      <h5>3. Acceptable use</h5>
      <p>When using the Service, you agree not to:</p>
      <ul>
        <li>Violate any applicable law or regulation.</li>
        <li>Upload or share content that is unlawful, harmful, misleading, infringing, or violates others’ rights.</li>
        <li>Attempt to reverse engineer, disrupt, overload, scrape, or impair the Service or its security.</li>
        <li>Access or use the Service to build a competing product or service.</li>
        <li>Use automated means or excessive request rates without permission.</li>
        <li>Misrepresent food, allergy, nutrition, or health-related information in a way that could cause harm.</li>
      </ul>

      <h5>4. Your content</h5>
      <p>
        You retain ownership of content you submit, such as recipes, meal plans, shopping lists, comments, and household
        information. You grant {brandName} a non-exclusive, worldwide, royalty-free licence to host, store, copy,
        display, process, and transmit your content solely to operate, secure, support, and improve the Service.
      </p>
      <p>
        You are responsible for ensuring you have the rights needed to submit content and that your content is accurate
        enough for your own use.
      </p>

      <h5>5. Intellectual property</h5>
      <p>
        We and our licensors own the Service, software, design, branding, and content we provide, excluding your content.
        These Terms do not grant you rights to our trademarks, logos, source code, or other brand features.
      </p>

      <h5>6. Third-party services</h5>
      <p>
        The Service may integrate with third-party providers for hosting, feature flags, analytics, AI, error
        monitoring, communications, or other operations. Your use of third-party services may be subject to their
        terms and policies. See our {renderLink({ href: "/privacy", children: "Privacy Policy" })} for more information
        about vendors.
      </p>

      <h5>7. Food, nutrition, and AI disclaimer</h5>
      <p>
        Recipes, ingredients, allergens, nutrition estimates, meal plans, shopping items, and AI-assisted outputs may be
        inaccurate, incomplete, or unsuitable for your circumstances. The Service is not professional medical, dietetic,
        allergy, or food safety advice. Read our {renderLink({ href: "/disclaimer", children: "Disclaimer" })} before
        relying on food, nutrition, health, or AI-assisted information.
      </p>

      <h5>8. Changes to the Service</h5>
      <p>
        We may add, change, suspend, or remove features, and may discontinue parts of the Service. Where reasonable, we
        will try to provide notice of material changes.
      </p>

      <h5>9. Suspension and termination</h5>
      <p>
        We may suspend or terminate access if you violate these Terms, create risk for users or systems, or if we are
        investigating suspected misconduct. You may stop using the Service at any time. Provisions that by their nature
        should continue after termination will continue to apply.
      </p>

      <h5>10. Warranties</h5>
      <p>
        To the maximum extent permitted by law, the Service is provided "as is" and "as available" without warranties of
        any kind, whether express, implied, or statutory, including implied warranties of merchantability, fitness for a
        particular purpose, and non-infringement.
      </p>

      <h5>11. Limitation of liability</h5>
      <p>
        To the maximum extent permitted by law, {legalEntityName} will not be liable for indirect, incidental, special,
        consequential, exemplary, or punitive damages, or for loss of profits, revenue, data, goodwill, or other
        intangible losses resulting from your use of or inability to use the Service.
      </p>

      <h5>12. Indemnity</h5>
      <p>
        You agree to indemnify and hold harmless {legalEntityName} from claims, liabilities, damages, losses, and
        expenses, including reasonable legal fees, arising from your use of the Service, your content, or your violation
        of these Terms.
      </p>

      <h5>13. Governing law</h5>
      <p>
        These Terms and any dispute or claim arising out of or in connection with them are governed by the laws of{" "}
        {governingLaw}. The {jurisdiction} will have exclusive jurisdiction, except that either party may seek interim
        relief in any court of competent jurisdiction.
      </p>

      <h5>14. Changes to these Terms</h5>
      <p>
        We may update these Terms from time to time. We will post the updated Terms with the new effective date. If
        changes are material, we may provide additional notice.
      </p>
    </>
  ),
};
